import { Loader2 } from "lucide-react";
import React from "react";
import { cn } from "../../../lib/utils";
import type { ButtonProps } from "./types";
import { ButtonSize, ButtonVariant } from "./types";

export type { ButtonProps } from "./types";
export { ButtonSize, ButtonVariant } from "./types";

const Button: React.FC<ButtonProps> = React.forwardRef<
	HTMLButtonElement,
	ButtonProps
>(
	(
		{
			className,
			variant = ButtonVariant.CONTAINED,
			size = ButtonSize.MEDIUM,
			fullWidth = false,
			loading = false,
			children,
			disabled,
			...props
		},
		ref,
	) => {
		const baseStyles =
			"inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

		const sizeStyles = {
			[ButtonSize.SMALL]: "h-9 px-4 text-xs",
			[ButtonSize.MEDIUM]: "h-11 px-8 py-2 text-sm",
			[ButtonSize.LARGE]: "h-14 px-12 text-base",
		};

		const getVariantClasses = () => {
			switch (variant) {
				case ButtonVariant.CONTAINED:
					return "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500";
				case ButtonVariant.OUTLINED:
					return "border-2 border-blue-600 text-blue-600 bg-transparent hover:bg-blue-50 focus-visible:ring-blue-500";
				case ButtonVariant.TEXT:
					return "text-blue-600 hover:bg-blue-50 focus-visible:ring-blue-500";
				default:
					return "bg-blue-600 text-white hover:bg-blue-700";
			}
		};

		return (
			<button
				ref={ref}
				className={cn(
					baseStyles,
					sizeStyles[size],
					getVariantClasses(),
					fullWidth && "w-full",
					className,
				)}
				disabled={disabled || loading}
				{...props}
			>
				{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
				{children}
			</button>
		);
	},
);

Button.displayName = "Button";

export { Button };
